import React, { useState, useEffect, useContext } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Table,
  Badge,
  Spinner,
  Alert,
  Button,
  InputGroup,
  Form,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";
import {
  IconRocket,
  IconSearch,
  IconFilter,
  IconEye,
  IconBolt,
  IconPlayerPause,
  IconX,
  IconAlertTriangle,
  IconSortAscending,
  IconSortDescending,
} from "@tabler/icons-react";
import Swal from "sweetalert2";
import UserContext from "../../contexts/UserContext";
import {
  listAgentDeployments,
  activateAgentDeployment,
  suspendAgentDeployment,
  cancelAgentDeployment,
  buildSearchParams,
  formatDeploymentData,
  canPerformAction,
} from "./services/AgentDeploymentServices";
import AgentDeploymentModal from "./modals/AgentDeploymentModal";

const AgentDeploymentsList = () => {
  const { user, isOrganization } = useContext(UserContext);
  
  // Estados principais
  const [loading, setLoading] = useState(false);
  const [deployments, setDeployments] = useState([]);
  const [selectedDeploymentId, setSelectedDeploymentId] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  
  // Estados de paginação
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const pageSize = 15;

  // Estados de filtros
  const [filters, setFilters] = useState({
    search: "",
    status: "all",
    subaccount: "",
    support_type: "all",
    setup_fee_paid: undefined,
    ordering: "-deployed_at", // Mais recentes primeiro
  });

  // Estados de UI
  const [actionLoading, setActionLoading] = useState({});

  useEffect(() => {
    loadDeployments();
  }, [currentPage, filters]);

  const loadDeployments = async () => {
    try {
      setLoading(true);
      
      const searchParams = buildSearchParams({
        ...filters,
        page: currentPage,
        page_size: pageSize,
      });

      const response = await listAgentDeployments(searchParams);
      const data = response.data;

      // Formatar dados para exibição
      const formattedDeployments = data.results?.map(deployment => 
        formatDeploymentData(deployment)
      ) || [];

      setDeployments(formattedDeployments);
      setTotalPages(data.total_pages || 1);
      setTotalCount(data.count || 0);
    } catch (error) {
      console.error("Erro ao carregar implementações:", error);
      Swal.fire({
        icon: "error",
        title: "Erro",
        text: "Não foi possível carregar as implementações de agentes.",
        confirmButtonColor: "#dc3545",
      });
    } finally {
      setLoading(false);
    }
  };

  // Atualizar filtros
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setCurrentPage(1); // Reset para primeira página
  };

  // Busca
  const handleSearch = (value) => {
    handleFilterChange("search", value);
  };

  // Ações de status
  const handleStatusAction = async (deploymentId, action) => {
    const deployment = deployments.find(d => d.id === deploymentId);
    if (!deployment) return;

    // Verificar permissões
    if (!canPerformAction(action, { user, isOrganization }, deployment)) {
      Swal.fire({
        icon: "warning",
        title: "Sem Permissão",
        text: "Você não tem permissão para executar esta ação.",
      });
      return;
    }

    const actionMap = {
      activate: {
        fn: activateAgentDeployment,
        title: "ativação",
        text: "ativar",
        description: `Você tem certeza que deseja ativar a implementação do agente "${deployment.agent?.name}" para a empresa "${deployment.subaccount?.company_name}"?`,
        color: "success",
      },
      suspend: {
        fn: suspendAgentDeployment,
        title: "suspenção",
        text: "suspender",
        description: `Você tem certeza que deseja suspender a implementação do agente "${deployment.agent?.name}" para a empresa "${deployment.subaccount?.company_name}"?`,
        color: "warning",
      },
      cancel: {
        fn: cancelAgentDeployment,
        title: "cancelamento",
        text: "cancelar",
        description: `Você tem certeza que deseja cancelar a implementação do agente "${deployment.agent?.name}" para a empresa "${deployment.subaccount?.company_name}"? <br><br><b class='text-danger'>Esta ação irá DELETAR O AGENTE para este cliente e não pode ser desfeita.</b>`,
        color: "danger",
      },
    };

    const actionConfig = actionMap[action];
    if (!actionConfig) return;

    // Confirmação
    const result = await Swal.fire({
      title: `Confirmar ${actionConfig.title}`,
      html: actionConfig.description,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: `Sim, ${actionConfig.text}`,
      cancelButtonText: "Cancelar",
      confirmButtonColor:
        actionConfig.color === "success"
          ? "#28a745"
          : actionConfig.color === "warning"
          ? "#ffc107"
          : "#dc3545",
    });

    if (!result.isConfirmed) return;

    try {
      setActionLoading(prev => ({ ...prev, [deploymentId]: action }));
      
      await actionConfig.fn(deploymentId);
      
      Swal.fire({
        icon: "success",
        title: "Sucesso!",
        text: `Implementação ${actionConfig.text === "ativar" ? "ativada" : actionConfig.text === "suspender" ? "suspensa" : "cancelada"} com sucesso.`,
        timer: 2000,
        showConfirmButton: false,
      });

      // Recarregar lista
      loadDeployments();
    } catch (error) {
      let errorMessage =
        error.response?.data?.error ||
        `Não foi possível ${actionConfig.text} a implementação.`;
      console.error(`Erro ao ${actionConfig.text}:`, error);
      Swal.fire({
        icon: "warning",
        title: "Oops!",
        text: errorMessage,
        confirmButtonColor: "#dc3545",
      });
    } finally {
      setActionLoading(prev => ({ ...prev, [deploymentId]: false }));
    }
  };

  // Visualizar detalhes
  const handleViewDetails = (deploymentId) => {
    if (!canPerformAction("view", { user, isOrganization })) {
      Swal.fire({
        icon: "warning",
        title: "Sem Permissão",
        text: "Você não tem permissão para visualizar detalhes.",
      });
      return;
    }

    setSelectedDeploymentId(deploymentId);
    setShowDetailModal(true);
  };

  // Sucesso do modal
  const handleModalSuccess = () => {
    loadDeployments();
  };

  // Renderizar paginação
  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const pages = [];
    const maxVisible = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let endPage = Math.min(totalPages, startPage + maxVisible - 1);

    if (endPage - startPage + 1 < maxVisible) {
      startPage = Math.max(1, endPage - maxVisible + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <Button
          key={i}
          variant={i === currentPage ? "primary" : "outline-primary"}
          size="sm"
          className="me-1"
          onClick={() => setCurrentPage(i)}
        >
          {i}
        </Button>
      );
    }

    return (
      <div className="d-flex justify-content-center align-items-center mt-3">
        <Button
          variant="outline-primary"
          size="sm"
          className="me-2"
          disabled={currentPage === 1}
          onClick={() => setCurrentPage(currentPage - 1)}
        >
          Anterior
        </Button>
        {pages}
        <Button
          variant="outline-primary"
          size="sm"
          className="ms-2"
          disabled={currentPage === totalPages}
          onClick={() => setCurrentPage(currentPage + 1)}
        >
          Próximo
        </Button>
      </div>
    );
  };

  // Renderizar ações da linha
  const renderRowActions = (deployment) => {
    const isLoading = actionLoading[deployment.id];
    
    return (
      <div className="d-flex gap-1">
        {/* Visualizar */}
        <OverlayTrigger
          overlay={<Tooltip>Ver detalhes</Tooltip>}
        >
          <Button
            variant="outline-primary"
            size="sm"
            onClick={() => handleViewDetails(deployment.id)}
            disabled={isLoading}
          >
            <IconEye size={14} />
          </Button>
        </OverlayTrigger>

        {/* Ativar */}
        {deployment.status !== "active" && (
          <OverlayTrigger
            overlay={<Tooltip>Ativar</Tooltip>}
          >
            <Button
              variant="outline-success"
              size="sm"
              onClick={() => handleStatusAction(deployment.id, "activate")}
              disabled={isLoading || !canPerformAction("activate", { user, isOrganization }, deployment)}
            >
              {isLoading === "activate" ? <Spinner size="sm" /> : <IconBolt size={14} />}
            </Button>
          </OverlayTrigger>
        )}

        {/* Suspender */}
        {deployment.status === "active" && (
          <OverlayTrigger
            overlay={<Tooltip>Suspender</Tooltip>}
          >
            <Button
              variant="outline-warning"
              size="sm"
              onClick={() => handleStatusAction(deployment.id, "suspend")}
              disabled={isLoading || !canPerformAction("suspend", { user, isOrganization }, deployment)}
            >
              {isLoading === "suspend" ? <Spinner size="sm" /> : <IconPlayerPause size={14} />}
            </Button>
          </OverlayTrigger>
        )}

        {/* Cancelar */}
        {deployment.status !== "canceled" && (
          <OverlayTrigger
            overlay={<Tooltip>Cancelar</Tooltip>}
          >
            <Button
              variant="outline-danger"
              size="sm"
              onClick={() => handleStatusAction(deployment.id, "cancel")}
              disabled={isLoading || !canPerformAction("cancel", { user, isOrganization }, deployment)}
            >
              {isLoading === "cancel" ? <Spinner size="sm" /> : <IconX size={14} />}
            </Button>
          </OverlayTrigger>
        )}
      </div>
    );
  };

  // Renderizar ordenação no header
  const renderSortIcon = (field) => {
    if (filters.ordering === field) {
      return <IconSortAscending size={14} className="ms-1" />;
    }
    if (filters.ordering === `-${field}`) {
      return <IconSortDescending size={14} className="ms-1" />;
    }
    return null;
  };

  const handleSort = (field) => {
    const currentOrdering = filters.ordering;
    let newOrdering;
    
    if (currentOrdering === field) {
      newOrdering = `-${field}`;
    } else if (currentOrdering === `-${field}`) {
      newOrdering = field;
    } else {
      newOrdering = `-${field}`;
    }
    
    handleFilterChange("ordering", newOrdering);
  };

  return (
      <Container>
        {/* Header */}
        <div className="mb-4">
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h3 className="d-flex align-items-center">
                <IconRocket size={30} className="me-2" />
                Implementações de Agentes
              </h3>
              <p className="text-muted">
                Gerencie as implementações de agentes IA para seus clientes
              </p>
            </div>
          </div>
        </div>

        {/* Filtros e Busca */}
        <Card className="mb-4">
          <Card.Body>
            <Row>
              <Col xs={12} md={3}>
                <InputGroup>
                  <InputGroup.Text>
                    <IconSearch size={16} />
                  </InputGroup.Text>
                  <Form.Control
                    type="text"
                    placeholder="Buscar por agente ou empresa..."
                    value={filters.search}
                    onChange={(e) => handleSearch(e.target.value)}
                  />
                </InputGroup>
              </Col>
              
              <Col xs={12} md={3}>
                <Form.Select
                  value={filters.status}
                  onChange={(e) => handleFilterChange("status", e.target.value)}
                >
                  <option value="all">Todos os Status</option>
                  <option value="active">Ativo</option>
                  <option value="suspended">Suspenso</option>
                  <option value="canceled">Cancelado</option>
                </Form.Select>
              </Col>

              <Col xs={12} md={3}>
                <Form.Select
                  value={filters.support_type}
                  onChange={(e) => handleFilterChange("support_type", e.target.value)}
                >
                  <option value="all">Tipo de Suporte</option>
                  <option value="premium">Premium</option>
                  <option value="basic">Básico</option>
                </Form.Select>
              </Col>

              <Col xs={12} md={3}>
                <Form.Select
                  value={filters.setup_fee_paid === undefined ? "all" : filters.setup_fee_paid ? "true" : "false"}
                  onChange={(e) => {
                    const value = e.target.value === "all" ? undefined : e.target.value === "true";
                    handleFilterChange("setup_fee_paid", value);
                  }}
                >
                  <option value="all">Setup Fee</option>
                  <option value="true">Pago</option>
                  <option value="false">Pendente</option>
                </Form.Select>
              </Col>

            
            </Row>
          </Card.Body>
        </Card>

        {/* Tabela de Implementações */}
        <Card>
          <Card.Header className="d-flex gap-2 align-items-center">
            <IconRocket size={20} />
            <h5 className="mb-0">Lista de Implementações</h5>
          </Card.Header>
          <Card.Body>
            {loading ? (
              <div
                className="d-flex justify-content-center align-items-center"
                style={{ minHeight: "300px" }}
              >
                <div className="text-center">
                  <Spinner animation="border" variant="primary" />
                  <p className="mt-3">Carregando implementações...</p>
                </div>
              </div>
            ) : deployments.length > 0 ? (
              <>
                <Table responsive hover>
                  <thead>
                    <tr>
                      <th 
                        style={{ cursor: "pointer" }}
                        onClick={() => handleSort("agent__name")}
                      >
                        Agente {renderSortIcon("agent__name")}
                      </th>
                      <th>Cliente</th>
                      <th 
                        style={{ cursor: "pointer" }}
                        onClick={() => handleSort("status")}
                      >
                        Status {renderSortIcon("status")}
                      </th>
                      <th 
                        style={{ cursor: "pointer" }}
                        onClick={() => handleSort("setup_fee_paid")}
                      >
                        Taxa Setup {renderSortIcon("setup_fee_paid")}
                      </th>
                      <th 
                        style={{ cursor: "pointer" }}
                        onClick={() => handleSort("monthly_fee")}
                      >
                        Mensalidade {renderSortIcon("monthly_fee")}
                      </th>
                      <th 
                        style={{ cursor: "pointer" }}
                        onClick={() => handleSort("deployed_at")}
                      >
                        Data Ativação {renderSortIcon("deployed_at")}
                      </th>
                      <th>Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {deployments.map((deployment) => (
                      <tr 
                        key={deployment.id}
                        style={{ cursor: "pointer" }}
                        onClick={() => handleViewDetails(deployment.id)}
                      >
                        <td>
                          <div>
                            <strong>{deployment.agent?.name || "—"}</strong>
                            {deployment.agent?.area && (
                              <small className="d-block text-muted">
                                {deployment.agent.area}
                              </small>
                            )}
                          </div>
                        </td>
                        <td>
                          <div>
                            <span>{deployment.subaccount?.company_name || "—"}</span>
                            {deployment.subaccount?.brand_name && (
                              <small className="d-block text-muted">
                                {deployment.subaccount.brand_name}
                              </small>
                            )}
                          </div>
                        </td>
                        <td>
                          <Badge bg={deployment.statusVariant}>
                            {deployment.statusText}
                          </Badge>
                        </td>
                        <td>
                          <Badge bg={deployment.setupFeePaidVariant}>
                            {deployment.setupFeePaidText}
                          </Badge>
                        </td>
                        <td>
                          <strong>{deployment.formattedMonthlyFee}</strong>
                        </td>
                       
                        <td>
                          <span>{deployment.formattedDeployedAt}</span>
                        </td>
                        <td onClick={(e) => e.stopPropagation()}>
                          {renderRowActions(deployment)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
                {renderPagination()}
              </>
            ) : (
              <Alert variant="info" className="text-center">
                <IconRocket size={24} className="mb-2" />
                <p className="mb-0">
                  {filters.search || filters.status !== "all" || filters.support_type !== "all" || filters.setup_fee_paid !== undefined
                    ? "Nenhuma implementação encontrada com os filtros aplicados."
                    : "Nenhuma implementação encontrada."}
                </p>
                <small>
                  {filters.search || filters.status !== "all" || filters.support_type !== "all" || filters.setup_fee_paid !== undefined
                    ? "Tente ajustar os filtros de busca."
                    : "As implementações aparecerão aqui quando forem criadas."}
                </small>
              </Alert>
            )}
          </Card.Body>
        </Card>

        {/* Modal de Detalhes */}
        <AgentDeploymentModal
          show={showDetailModal}
          onHide={() => setShowDetailModal(false)}
          deploymentId={selectedDeploymentId}
          onSuccess={handleModalSuccess}
        />
      </Container>
  );
};

export default AgentDeploymentsList;